import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, API, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, Download, Pin, PinOff } from "lucide-react";
import { parseReel } from "@/components/Reels";

const TABS = ["Overview","Events","Athletes","Partners","Reels","Registrations","Enquiries","Media","Account"];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Overview");

  if (loading) return <div className="p-12 text-center text-[#888]">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      <div className="border-b border-[#2a2a2a]">
        <div className="max-w-[1600px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <div>
            <div className="font-display text-sm">Ironveil Admin</div>
            <div className="type-meta text-[#666]">{user.email}</div>
          </div>
          <button onClick={async () => { await logout(); nav("/admin/login"); }} className="btn-text" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
        </div>
        <div className="max-w-[1600px] mx-auto px-5 lg:px-10 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 type-meta whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-[#C8102E] text-white" : "border-transparent text-[#888] hover:text-white"}`}
              data-testid={`admin-tab-${t.toLowerCase()}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-5 lg:px-10 py-10">
        {tab === "Overview" && <Overview />}
        {tab === "Events" && <EventsAdmin />}
        {tab === "Athletes" && <AthletesAdmin />}
        {tab === "Partners" && <PartnersAdmin />}
        {tab === "Reels" && <ReelsAdmin />}
        {tab === "Registrations" && <RegistrationsAdmin />}
        {tab === "Enquiries" && <EnquiriesAdmin />}
        {tab === "Media" && <MediaAdmin />}
        {tab === "Account" && <AccountAdmin email={user.email} />}
      </div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  const items = [
    ["Events", s.events],["Athletes", s.athletes],["Partners", s.partners],
    ["Registrations", s.registrations],["New Registrations", s.new_registrations],["Enquiries", s.enquiries],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-stats">
      {items.map(([l, v]) => (
        <div key={l} className="card-ive p-6">
          <div className="type-meta text-[#888]">{l}</div>
          <div className="font-display text-4xl mt-3">{v ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

// Generic uploader
async function uploadAdmin(file, kind = "image") {
  const fd = new FormData();
  fd.append("file", file); fd.append("kind", kind);
  const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

function EventsAdmin() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/events").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const empty = { title:"", event_number:"", sport:"MMA", slug:"", location:"", venue:"", date:"", status:"Coming Soon", description:"", rules:"", weight_categories:[], poster_path:"", hero_path:"" };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display">Events</h2>
        <button onClick={() => setEdit({ ...empty })} className="btn-primary" data-testid="admin-new-event"><Plus size={14} /> New Event</button>
      </div>
      <div className="card-ive divide-y divide-[#2a2a2a]">
        {rows.map((e) => (
          <div key={e.id} className="p-4 flex items-center justify-between gap-3">
            <div><div className="font-display text-sm">{e.title}</div><div className="text-xs text-[#888]">{e.sport} · {e.status} · /{e.slug}</div></div>
            <div className="flex gap-2">
              <button onClick={() => setEdit(e)} className="btn-text" data-testid={`edit-event-${e.slug}`}><Edit3 size={14} /></button>
              <button onClick={async () => { if(confirm("Archive event?")){ await api.delete(`/events/${e.id}`); load(); } }} className="btn-text" data-testid={`del-event-${e.slug}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!rows.length && <div className="p-6 text-center text-[#666]">No events yet.</div>}
      </div>
      {edit && <EventForm event={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EventForm({ event, onClose, onSaved }) {
  const [f, setF] = useState({ ...event, weight_categories: event.weight_categories || [] });
  const [wcInput, setWcInput] = useState((event.weight_categories || []).join(", "));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, weight_categories: wcInput.split(",").map(s => s.trim()).filter(Boolean) };
      if (f.id) await api.put(`/events/${f.id}`, payload);
      else await api.post("/events", payload);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  const uploadPoster = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const p = await uploadAdmin(file, "image"); set("poster_path", p); toast.success("Poster uploaded");
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4" data-testid="event-form-modal">
      <form onSubmit={submit} className="bg-[#0a0a0a] border border-[#2a2a2a] w-full max-w-3xl p-6 mt-8 grid grid-cols-2 gap-4">
        <h3 className="col-span-2 font-display text-lg">{f.id ? "Edit" : "New"} Event</h3>
        <Fld label="Title"><input value={f.title} required onChange={(e) => set("title", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Slug"><input value={f.slug} required onChange={(e) => set("slug", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Event Number"><input value={f.event_number || ""} onChange={(e) => set("event_number", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Sport"><select value={f.sport} onChange={(e) => set("sport", e.target.value)} className="w-full px-3 py-2"><option>MMA</option><option>Boxing</option></select></Fld>
        <Fld label="Status"><select value={f.status} onChange={(e) => set("status", e.target.value)} className="w-full px-3 py-2">{["Coming Soon","Registration Open","Registration Closed","Live","Completed"].map(s => <option key={s}>{s}</option>)}</select></Fld>
        <Fld label="Date"><input value={f.date || ""} onChange={(e) => set("date", e.target.value)} className="w-full px-3 py-2" placeholder="e.g. 15 March 2026" /></Fld>
        <Fld label="Location"><input value={f.location} required onChange={(e) => set("location", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Venue"><input value={f.venue || ""} onChange={(e) => set("venue", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Weight Categories (comma-sep)" full><input value={wcInput} onChange={(e) => setWcInput(e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Description" full><textarea rows={3} value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Rules" full><textarea rows={3} value={f.rules || ""} onChange={(e) => set("rules", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Poster" full>
          <input type="file" accept="image/*" onChange={uploadPoster} className="w-full px-3 py-2" />
          {f.poster_path && <img src={fileUrl(f.poster_path)} alt="poster" className="mt-2 h-32 border border-[#2a2a2a]" />}
        </Fld>
        <div className="col-span-2 flex justify-end gap-3 mt-4">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button disabled={saving} className="btn-primary" data-testid="save-event">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function AthletesAdmin() {
  const [rows, setRows] = useState([]); const [edit, setEdit] = useState(null);
  const load = () => api.get("/athletes").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const empty = { name:"", slug:"", sport:"MMA", level:"Amateur", weight_class:"Lightweight", city:"", state:"", academy:"", coach:"", wins:0, losses:0, draws:0, ko:0, ranking:null, photo_path:"", bio:"", height:"", reach:"", last_weigh_in:"", submissions:0, fight_history:[], featured:false };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display">Athletes</h2>
        <button onClick={() => setEdit({ ...empty })} className="btn-primary" data-testid="admin-new-athlete"><Plus size={14} /> New</button>
      </div>
      <div className="card-ive divide-y divide-[#2a2a2a]">
        {rows.map((a) => (
          <div key={a.id} className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {a.photo_path ? <img src={fileUrl(a.photo_path)} alt="" className="w-10 h-10 object-cover border border-[#2a2a2a]" /> : <div className="w-10 h-10 border border-[#2a2a2a] bg-black" />}
              <div>
                <div className="font-display text-sm">{a.name} {a.featured && <span className="text-[#C8102E] type-meta ml-2">FEATURED</span>}</div>
                <div className="text-xs text-[#888]">{a.sport} · {a.level} · {a.weight_class} · {a.wins}-{a.losses}-{a.draws}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEdit(a)} className="btn-text"><Edit3 size={14} /></button>
              <button onClick={async () => { if(confirm("Archive athlete?")){ await api.delete(`/athletes/${a.id}`); load(); } }} className="btn-text"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!rows.length && <div className="p-6 text-center text-[#666]">No athletes yet.</div>}
      </div>
      {edit && <AthleteForm athlete={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function AthleteForm({ athlete, onClose, onSaved }) {
  const [f, setF] = useState({ ...athlete });
  const [fhText, setFhText] = useState((athlete.fight_history || []).map((h) => [h.date, h.event, h.opponent, h.result, h.method].join(" | ")).join("\n"));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const uploadPhoto = async (e) => { const file = e.target.files?.[0]; if(!file) return; const p = await uploadAdmin(file, "image"); set("photo_path", p); toast.success("Uploaded"); };
  const submit = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, wins:+f.wins||0, losses:+f.losses||0, draws:+f.draws||0, ko:+f.ko||0, submissions:+f.submissions||0, ranking: f.ranking ? +f.ranking : null,
        fight_history: fhText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [date, event, opponent, result, method] = l.split("|").map((x) => x.trim()); return { date, event, opponent, result, method }; }) };
      if (f.id) await api.put(`/athletes/${f.id}`, payload); else await api.post("/athletes", payload);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4">
      <form onSubmit={submit} className="bg-[#0a0a0a] border border-[#2a2a2a] w-full max-w-3xl p-6 mt-8 grid grid-cols-2 gap-4">
        <h3 className="col-span-2 font-display text-lg">{f.id ? "Edit" : "New"} Athlete</h3>
        <Fld label="Name"><input value={f.name} required onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Slug"><input value={f.slug} required onChange={(e) => set("slug", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Sport"><select value={f.sport} onChange={(e) => set("sport", e.target.value)} className="w-full px-3 py-2"><option>MMA</option><option>Boxing</option></select></Fld>
        <Fld label="Level"><select value={f.level} onChange={(e) => set("level", e.target.value)} className="w-full px-3 py-2"><option>Amateur</option><option>Pro</option></select></Fld>
        <Fld label="Weight Class"><input value={f.weight_class} onChange={(e) => set("weight_class", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Ranking"><input type="number" value={f.ranking || ""} onChange={(e) => set("ranking", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="City"><input value={f.city} onChange={(e) => set("city", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="State"><input value={f.state} onChange={(e) => set("state", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Academy"><input value={f.academy} onChange={(e) => set("academy", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Coach"><input value={f.coach} onChange={(e) => set("coach", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Wins"><input type="number" value={f.wins} onChange={(e) => set("wins", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Losses"><input type="number" value={f.losses} onChange={(e) => set("losses", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Draws"><input type="number" value={f.draws} onChange={(e) => set("draws", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="KOs"><input type="number" value={f.ko} onChange={(e) => set("ko", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Submissions"><input type="number" value={f.submissions ?? 0} onChange={(e) => set("submissions", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Height"><input value={f.height || ""} onChange={(e) => set("height", e.target.value)} className="w-full px-3 py-2" placeholder={"e.g. 5'6\" / 168 cm"} /></Fld>
        <Fld label="Reach"><input value={f.reach || ""} onChange={(e) => set("reach", e.target.value)} className="w-full px-3 py-2" placeholder="N/A" /></Fld>
        <Fld label="Last weigh-in"><input value={f.last_weigh_in || ""} onChange={(e) => set("last_weigh_in", e.target.value)} className="w-full px-3 py-2" placeholder="e.g. 119.3 lbs" /></Fld>
        <Fld label="Fight History (one per line: date | event | opponent | result | method)" full>
          <textarea rows={3} value={fhText} onChange={(e) => setFhText(e.target.value)} className="w-full px-3 py-2 font-mono text-xs" placeholder="31 Aug 2024 | Matrix Fight Night 15 | Jojo Rajkumari | Win | TKO — Punches, R2 4:05" />
        </Fld>
        <Fld label="Photo" full>
          <input type="file" accept="image/*" onChange={uploadPhoto} className="w-full px-3 py-2" />
          {f.photo_path && <img src={fileUrl(f.photo_path)} alt="" className="mt-2 h-24 border border-[#2a2a2a]" />}
        </Fld>
        <Fld label="Bio" full><textarea rows={3} value={f.bio} onChange={(e) => set("bio", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Feature on homepage</label>
        <div className="col-span-2 flex justify-end gap-3 mt-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button disabled={saving} className="btn-primary">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function PartnersAdmin() {
  const [rows, setRows] = useState([]); const [edit, setEdit] = useState(null);
  const load = () => api.get("/partners").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const empty = { name:"", category:"Sponsor", logo_path:"", location:"", coach_name:"", sport:"", description:"", photo_path:"", website:"", order:0 };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display">Partners</h2>
        <button onClick={() => setEdit({ ...empty })} className="btn-primary"><Plus size={14} /> New</button>
      </div>
      <div className="card-ive divide-y divide-[#2a2a2a]">
        {rows.map((p) => (
          <div key={p.id} className="p-4 flex items-center justify-between">
            <div><div className="font-display text-sm">{p.name}</div><div className="text-xs text-[#888]">{p.category} · {p.location}</div></div>
            <div className="flex gap-2">
              <button onClick={() => setEdit(p)} className="btn-text"><Edit3 size={14} /></button>
              <button onClick={async () => { if(confirm("Archive partner?")){ await api.delete(`/partners/${p.id}`); load(); } }} className="btn-text"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!rows.length && <div className="p-6 text-center text-[#666]">No partners yet.</div>}
      </div>
      {edit && <PartnerForm p={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
function PartnerForm({ p, onClose, onSaved }) {
  const [f, setF] = useState({ ...p });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const uploadLogo = async (e) => { const file = e.target.files?.[0]; if(!file) return; const path = await uploadAdmin(file, "image"); set("logo_path", path); };
  const submit = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, order: +f.order || 0 };
      if (f.id) await api.put(`/partners/${f.id}`, payload); else await api.post("/partners", payload);
      toast.success("Saved"); onSaved();
    } catch(err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4">
      <form onSubmit={submit} className="bg-[#0a0a0a] border border-[#2a2a2a] w-full max-w-2xl p-6 mt-8 grid grid-cols-2 gap-4">
        <h3 className="col-span-2 font-display text-lg">{f.id ? "Edit" : "New"} Partner</h3>
        <Fld label="Name"><input value={f.name} required onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Category"><select value={f.category} onChange={(e) => set("category", e.target.value)} className="w-full px-3 py-2"><option>Technical</option><option>Academy</option><option>Sponsor</option></select></Fld>
        <Fld label="Location"><input value={f.location} onChange={(e) => set("location", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Head Coach"><input value={f.coach_name} onChange={(e) => set("coach_name", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Sport"><input value={f.sport || ""} onChange={(e) => set("sport", e.target.value)} className="w-full px-3 py-2" placeholder="MMA / Boxing" /></Fld>
        <Fld label="Description" full><textarea rows={2} value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Coach / Partner Photo" full>
          <input type="file" accept="image/*" onChange={async (e) => { const file = e.target.files?.[0]; if(!file) return; set("photo_path", await uploadAdmin(file, "image")); }} className="w-full px-3 py-2" />
          {f.photo_path && <img src={fileUrl(f.photo_path)} alt="" className="mt-2 h-16 border border-[#2a2a2a]" />}
        </Fld>
        <Fld label="Website"><input value={f.website} onChange={(e) => set("website", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Order"><input type="number" value={f.order} onChange={(e) => set("order", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Logo" full>
          <input type="file" accept="image/*" onChange={uploadLogo} className="w-full px-3 py-2" />
          {f.logo_path && <img src={fileUrl(f.logo_path)} alt="" className="mt-2 h-16 border border-[#2a2a2a]" />}
        </Fld>
        <div className="col-span-2 flex justify-end gap-3"><button type="button" onClick={onClose} className="btn-secondary">Cancel</button><button disabled={saving} className="btn-primary">Save</button></div>
      </form>
    </div>
  );
}

function ReelsAdmin() {
  const [rows, setRows] = useState([]); const [edit, setEdit] = useState(null);
  const load = () => api.get("/reels").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const empty = { title:"", caption:"", video_url:"", thumbnail_url:"", tag:"Training", order:0, pinned:true };
  const togglePin = async (r) => { await api.patch(`/reels/${r.id}/pin`); toast.success(r.pinned ? "Unpinned from homepage" : "Pinned to homepage"); load(); };
  return (
    <div data-testid="admin-reels">
      <div className="flex justify-between items-center mb-4">
        <div><h2 className="font-display">Media Reels</h2><div className="text-xs text-[#888] mt-1">Pinned reels appear on the homepage.</div></div>
        <button onClick={() => setEdit({ ...empty })} className="btn-primary" data-testid="admin-new-reel"><Plus size={14} /> New Reel</button>
      </div>
      <div className="card-ive divide-y divide-[#2a2a2a]">
        {rows.map((r) => {
          const p = parseReel(r.video_url); const thumb = r.thumbnail_url || p.thumb;
          return (
            <div key={r.id} className="p-4 flex items-center justify-between gap-3" data-testid={`reel-row-${r.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {thumb ? <img src={thumb} alt="" className="w-10 h-14 object-cover border border-[#2a2a2a]" /> : <div className="w-10 h-14 border border-[#2a2a2a] bg-black" />}
                <div className="min-w-0">
                  <div className="font-display text-sm truncate">{r.title} {r.pinned && <span className="text-[#C8102E] type-meta ml-2">PINNED</span>}</div>
                  <div className="text-xs text-[#888] truncate">{r.tag} · {p.kind} · order {r.order}</div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => togglePin(r)} className="btn-text" title={r.pinned ? "Unpin" : "Pin"} data-testid={`pin-reel-${r.id}`}>{r.pinned ? <PinOff size={14} /> : <Pin size={14} />}</button>
                <button onClick={() => setEdit(r)} className="btn-text" data-testid={`edit-reel-${r.id}`}><Edit3 size={14} /></button>
                <button onClick={async () => { if(confirm("Remove reel?")){ await api.delete(`/reels/${r.id}`); load(); } }} className="btn-text" data-testid={`del-reel-${r.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
        {!rows.length && <div className="p-6 text-center text-[#666]">No reels yet. Paste a YouTube, Instagram or .mp4 link to get started.</div>}
      </div>
      {edit && <ReelForm r={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
function ReelForm({ r, onClose, onSaved }) {
  const [f, setF] = useState({ ...r });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const p = parseReel(f.video_url);
  const submit = async (ev) => {
    ev.preventDefault();
    if (p.kind === "unknown") { toast.error("Use a YouTube, Instagram Reel or direct .mp4 link"); return; }
    setSaving(true);
    try {
      const payload = { ...f, order: +f.order || 0 };
      if (f.id) await api.put(`/reels/${f.id}`, payload); else await api.post("/reels", payload);
      toast.success("Saved"); onSaved();
    } catch(err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4" data-testid="reel-form-modal">
      <form onSubmit={submit} className="bg-[#0a0a0a] border border-[#2a2a2a] w-full max-w-2xl p-6 mt-8 grid grid-cols-2 gap-4">
        <h3 className="col-span-2 font-display text-lg">{f.id ? "Edit" : "New"} Reel</h3>
        <Fld label="Title" full><input value={f.title} required onChange={(e) => set("title", e.target.value)} className="w-full px-3 py-2" data-testid="reel-title" /></Fld>
        <Fld label="Video Link (YouTube / Instagram Reel / .mp4)" full>
          <input value={f.video_url} required onChange={(e) => set("video_url", e.target.value)} className="w-full px-3 py-2" placeholder="https://youtube.com/shorts/…" data-testid="reel-url" />
          {f.video_url && <div className={`type-meta mt-1 ${p.kind === "unknown" ? "text-[#C8102E]" : "text-[#888]"}`} data-testid="reel-url-kind">{p.kind === "unknown" ? "Unsupported link" : `Detected: ${p.kind}`}</div>}
        </Fld>
        <Fld label="Tag"><select value={f.tag} onChange={(e) => set("tag", e.target.value)} className="w-full px-3 py-2">{["Training","Fight","Highlight","Behind the Scenes","Announcement"].map(t => <option key={t}>{t}</option>)}</select></Fld>
        <Fld label="Order"><input type="number" value={f.order} onChange={(e) => set("order", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Caption" full><textarea rows={2} value={f.caption} onChange={(e) => set("caption", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <Fld label="Thumbnail URL (optional — auto for YouTube)" full><input value={f.thumbnail_url} onChange={(e) => set("thumbnail_url", e.target.value)} className="w-full px-3 py-2" /></Fld>
        <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.pinned} onChange={(e) => set("pinned", e.target.checked)} data-testid="reel-pinned" /> Pin to homepage</label>
        <div className="col-span-2 flex justify-end gap-3"><button type="button" onClick={onClose} className="btn-secondary">Cancel</button><button disabled={saving} className="btn-primary" data-testid="save-reel">{saving ? "Saving…" : "Save"}</button></div>
      </form>
    </div>
  );
}

function RegistrationsAdmin() {
  const [rows, setRows] = useState([]); const [q, setQ] = useState(""); const [status, setStatus] = useState("");
  const load = () => api.get("/registrations", { params: { q, status } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [q, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const setSt = async (id, s) => { await api.put(`/registrations/${id}/status`, { status: s }); load(); };
  const exportCsv = () => { window.open(`${API}/registrations/export.csv`, "_blank"); };
  const STATUSES = ["New","Under Review","Shortlisted","Confirmed","Rejected","Withdrawn"];
  return (
    <div data-testid="admin-registrations">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
        <h2 className="font-display">Registrations</h2>
        <div className="flex gap-2 flex-wrap">
          <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="px-3 py-2" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2"><option value="">All</option>{STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          <button onClick={exportCsv} className="btn-secondary" data-testid="admin-export-csv"><Download size={14} /> Export CSV</button>
        </div>
      </div>
      <div className="card-ive divide-y divide-[#2a2a2a]">
        {rows.map((r) => (
          <div key={r.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="font-display text-sm">{r.full_name}</div>
              <div className="text-xs text-[#888]">{r.sport} · {r.level} · {r.weight_class} · {r.email} · {r.phone}</div>
            </div>
            <select value={r.status} onChange={(e) => setSt(r.id, e.target.value)} className="px-3 py-2 text-xs" data-testid={`reg-status-${r.id}`}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        ))}
        {!rows.length && <div className="p-6 text-center text-[#666]">No registrations yet.</div>}
      </div>
    </div>
  );
}

function EnquiriesAdmin() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/partner-enquiries").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <h2 className="font-display mb-4">Partner Enquiries</h2>
      <div className="card-ive divide-y divide-[#2a2a2a]">
        {rows.map((r) => (
          <div key={r.id} className="p-4">
            <div className="font-display text-sm">{r.organization} — {r.partnership_type}</div>
            <div className="text-xs text-[#888] mt-1">{r.contact_person} · {r.email} · {r.phone}</div>
            <div className="text-sm mt-2 text-[#c9c9c9]">{r.message}</div>
          </div>
        ))}
        {!rows.length && <div className="p-6 text-center text-[#666]">No enquiries yet.</div>}
      </div>
    </div>
  );
}

function MediaAdmin() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/media").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    await uploadAdmin(file, file.type.startsWith("image/") ? "image" : "document");
    toast.success("Uploaded"); load(); e.target.value = "";
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display">Media Library</h2>
        <label className="btn-primary cursor-pointer"><input type="file" onChange={onUpload} className="hidden" />Upload</label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {rows.filter(r => r.content_type?.startsWith("image/")).map((m) => (
          <div key={m.id} className="card-ive aspect-square relative group">
            <img src={fileUrl(m.storage_path)} alt="" className="w-full h-full object-cover" />
            <button onClick={async () => { await api.delete(`/media/${m.id}`); load(); }} className="absolute top-2 right-2 bg-black/70 p-1"><Trash2 size={12} /></button>
          </div>
        ))}
        {!rows.length && <div className="col-span-full p-6 text-center text-[#666]">No media uploaded.</div>}
      </div>
    </div>
  );
}

function AccountAdmin({ email }) {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (ev) => {
    ev.preventDefault();
    if (f.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (f.next !== f.confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Password updated"); setF({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-md" data-testid="admin-account">
      <h2 className="font-display mb-1">Account</h2>
      <div className="text-xs text-[#888] mb-6">Signed in as {email}</div>
      <form onSubmit={submit} className="card-ive p-6 grid gap-4">
        <h3 className="font-display text-base">Change password</h3>
        <Fld label="Current password" full><input type="password" required autoComplete="current-password" value={f.current} onChange={(e) => set("current", e.target.value)} className="w-full px-3 py-2" data-testid="pw-current" /></Fld>
        <Fld label="New password (min 8 characters)" full><input type="password" required minLength={8} autoComplete="new-password" value={f.next} onChange={(e) => set("next", e.target.value)} className="w-full px-3 py-2" data-testid="pw-new" /></Fld>
        <Fld label="Confirm new password" full><input type="password" required autoComplete="new-password" value={f.confirm} onChange={(e) => set("confirm", e.target.value)} className="w-full px-3 py-2" data-testid="pw-confirm" /></Fld>
        <div className="flex justify-end"><button disabled={saving} className="btn-primary" data-testid="pw-save">{saving ? "Updating…" : "Update password"}</button></div>
      </form>
    </div>
  );
}

function Fld({ label, children, full }) {
  return <label className={`block ${full ? "col-span-2" : ""}`}><div className="type-meta text-[#888] mb-1">{label}</div>{children}</label>;
}
